import { initializeApp } from "firebase/app";
import {
    connectAuthEmulator,
    getAuth,
    GoogleAuthProvider,
} from "firebase/auth";
import {
    addDoc,
    collection,
    getDocs,
    getFirestore,
    query,
    orderBy,
    limit,
    startAfter,
    Timestamp,
    setDoc,
    doc,
    getDoc,
    serverTimestamp,
    updateDoc,
    deleteDoc,
    writeBatch,
    where,
    arrayUnion,
    connectFirestoreEmulator,
    onSnapshot,
    runTransaction,
} from "firebase/firestore";
import {
    connectFunctionsEmulator,
    getFunctions,
    httpsCallable,
} from "firebase/functions";
import {
    getStorage,
    ref,
    uploadBytesResumable,
    getDownloadURL,
    connectStorageEmulator,
} from "firebase/storage";
import {
    AGREEMENT_VERSION,
    fighterRequestStatus,
    fighterRequestType,
    paymentMethod,
    tokenStatus,
} from "../constants";
import { getLiveStreamUrls } from "./common";
import {
    generateJudgePin,
    generateJudgeToken,
    nextJudgeName,
} from "./judge-access";
import {
    initializeAppCheck,
    ReCaptchaEnterpriseProvider,
} from "firebase/app-check";

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

if (process.env.NODE_ENV === "development") {
    /* eslint-disable-next-line no-restricted-globals */
    self.FIREBASE_APPCHECK_DEBUG_TOKEN =
        process.env.REACT_APP_FIREBASE_APP_CHECK_DEBUG_TOKEN_FROM_CI;
}

export const appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(
        process.env.REACT_APP_FIREBASE_APP_CHECK_SITE_KEY
    ),
    isTokenAutoRefreshEnabled: true,
});

export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);
export const storage = getStorage(app);
export const provider = new GoogleAuthProvider();

// Emulator switch. Only honored in development builds so a prod bundle can
// never accidentally point at localhost. Two ways in:
//
//   npm run emulator   — starts the suite, seeds it, runs the dev server
//                        with this var already set. Nothing to toggle.
//   .env.local         — `REACT_APP_USE_EMULATOR=true` at the repo root, for
//                        pointing a plain `npm run dev` at an already-running
//                        suite. Restart the dev server after editing it.
//
// Defaults to OFF when the var is unset, empty, or anything other than the
// literal string "true".
const USE_EMULATOR =
    process.env.NODE_ENV === "development" &&
    process.env.REACT_APP_USE_EMULATOR === "true";

if (USE_EMULATOR) {
    // eslint-disable-next-line no-console
    console.warn(
        "[firebase] Emulator mode ON — Auth:9099 Firestore:8080 Functions:5001 Storage:9199"
    );
    connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
    connectFirestoreEmulator(db, "localhost", 8080);
    connectFunctionsEmulator(functions, "localhost", 5001);
    connectStorageEmulator(storage, "localhost", 9199);
}

export const usersCollection = collection(db, "users");
export const fightersCollection = collection(db, "fighters");
export const eventsCollection = collection(db, "events");
export const eventFightersCollection = collection(db, "event-fighters");
export const fighterRequestsCollection = collection(db, "fighter-requests");
export const rankingsCollection = collection(db, "rankings");
export const statisticsCollection = collection(db, "statistics");
export const liveStreamTicketsCollection = collection(
    db,
    "live-stream-tickets"
);
export const ordersCollection = collection(db, "orders");
export const errorLogsCollection = collection(db, "error-logs");
export const agreementsCollection = collection(db, "agreements");
export const fighterTokensCollection = collection(db, "fighter-tokens");
// Judge access. Both are admin-only in firestore.rules. The link token lives
// in its own collection, not on events/{id}, because the public getEvent
// callable returns the whole event doc to anyone.
export const eventJudgeLinksCollection = collection(db, "event-judge-links");
export const eventJudgesCollection = collection(db, "event-judges");
// Written only by the judge Cloud Functions; admins may read (Matched tab).
export const judgeScorecardsCollection = collection(db, "judge-scorecards");
// Partner Data API (admin read only; written by Cloud Functions).
export const partnerApiKeysCollection = collection(db, "partner-api-keys");
export const partnerApiUsageCollection = collection(db, "partner-api-usage");

export const approveFighter = httpsCallable(functions, "approveFighter");
export const approveRegister = httpsCallable(functions, "approveRegister");
export const approveUpdateInfo = httpsCallable(functions, "approveUpdateInfo");
export const approveWeightChange = httpsCallable(
    functions,
    "approveWeightChange"
);
export const updateRecords = httpsCallable(functions, "updateRecords");
export const deleteFighter = httpsCallable(functions, "deleteFighter");
export const createFighterAccount = httpsCallable(
    functions,
    "createFighterAccount"
);
export const deleteUser = httpsCallable(functions, "deleteUser");
export const addFighterToEvent = httpsCallable(functions, "addFighterToEvent");
export const removeFighterFromEvent = httpsCallable(
    functions,
    "removeFighterFromEvent"
);
export const recordAgreementCallable = httpsCallable(functions, "recordAgreement");
export const issueFighterTokenCallable = httpsCallable(functions, "issueFighterToken");
export const refundEventFighterCallable = httpsCallable(
    functions,
    "refundEventFighter"
);
export const rejoinEventFighterWithTokenCallable = httpsCallable(
    functions,
    "rejoinEventFighterWithToken"
);

/**
 * Refund a fighter for an event: issues 1 credit/token and records the refund
 * on the event-fighter doc (admin only). The fighter stays in the event and
 * remains visible publicly as unmatched. Distinct from pulling out.
 */
export const refundEventFighter = async ({ eventId, fighterId }) => {
    try {
        const result = await refundEventFighterCallable({ eventId, fighterId });
        return result?.data ?? null;
    } catch (error) {
        await logError(fighterId, "refundEventFighter", error, { eventId });
        throw error;
    }
};

/**
 * Re-activate a refunded fighter by spending one of their active credits
 * (admin only), so they can be matched again.
 */
export const rejoinEventFighterWithToken = async ({ eventId, fighterId }) => {
    try {
        const result = await rejoinEventFighterWithTokenCallable({
            eventId,
            fighterId,
        });
        return result?.data ?? null;
    } catch (error) {
        await logError(fighterId, "rejoinEventFighterWithToken", error, {
            eventId,
        });
        throw error;
    }
};

export const pullOutFighter = async (eventId, fighterId, currentIsPulledOut) => {
    try {
        const eventFighterRef = doc(
            eventFightersCollection,
            `${eventId}_${fighterId}`
        );
        await updateDoc(eventFighterRef, {
            isPulledOut: !currentIsPulledOut,
            updateAt: serverTimestamp(),
        });
        return true;
    } catch (error) {
        await logError(fighterId, "pullOutFighter", error, { eventId });
        throw error;
    }
};

export const createFighter = async (fighterData) => {
    const {
        name,
        nationality,
        email,
        birthdate,
        age,
        gender,
        phoneNumber,
        emergencyContactNumber,
        icOrPassport,
        gymNameOrTeamName,
        weightClass,
        height,
        records,
        walkOutMusic,
        igUsername,
        fighterPicture,
    } = fighterData;

    try {
        const data = {
            name: name ?? null,
            email: email ?? null,
            nationality: nationality ?? null,
            birthdate: birthdate ?? null,
            age: age || 0,
            gender: gender || null,
            phoneNumber: phoneNumber || null,
            emergencyContactNumber: emergencyContactNumber || null,
            icOrPassport: icOrPassport || null,
            gymNameOrTeamName: gymNameOrTeamName || null,
            weightClass: weightClass || null,
            height: height || 0,
            records: records || null,
            walkOutMusic: walkOutMusic || null,
            igUsername: igUsername || null,
            fighterPicture: fighterPicture || null,
            createdAt: serverTimestamp(),
            updateAt: serverTimestamp(),
        };

        const docRef = await addDoc(fightersCollection, data);

        return {
            ...data,
            id: docRef?.id,
        };
    } catch (error) {
        await logError(null, "createFighter", error, {
            email: fighterData.email,
        });
        return null;
    }
};

export const getFighters = async ({
    size = 10,
    lastDoc = null,
    order = "name",
    sort = "desc",
}) => {
    try {
        let q = query(
            fightersCollection,
            orderBy(order, sort)
            // ,  limit(size)
        );

        if (!!lastDoc) {
            q = query(
                fightersCollection,
                orderBy(order, sort),
                // limit(size),
                startAfter(lastDoc)
            );
        }

        const querySnapshot = await getDocs(q);

        const data =
            querySnapshot.size < 1
                ? []
                : querySnapshot.docs
                      .map((doc) => ({
                          id: doc.id,
                          ...doc.data(),
                      }))
                      .filter((fighter) => !fighter.deletedAt); // Filter deleted fighters client-side

        const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
        return { data, lastVisible };
    } catch (error) {
        await logError(null, "getFighters", error);
        return null;
    }
};

//  {
//     pathPrefix?: string;
//     fileName?: string;
//     metadata?: Record<string, string>;
//     onProgress?: (progress: number) => void;
//     allowedExtensions?: string[];
//     maxFileSize?: number; // in bytes
//   }
export const uploadFile = async (
    file,
    id,
    options = {
        maxFileSize: 25 * 1024 * 1024, // 25MB
    }
) => {
    // Validate file
    if (options.maxFileSize && file.size > options.maxFileSize) {
        throw new Error(
            `File exceeds maximum size of ${options.maxFileSize} bytes`
        );
    }

    const extension = file.name.split(".").pop()?.toLowerCase() ?? "bin";
    if (
        options.allowedExtensions &&
        !options.allowedExtensions.includes(extension)
    ) {
        throw new Error(
            `Invalid file type. Allowed: ${options.allowedExtensions.join(
                ", "
            )}`
        );
    }
    console.log(file);

    // Configure storage path
    const fileName = options.fileName || `${id}.${extension}`;
    const pathPrefix = options.pathPrefix || "uploads";
    const storagePath = `${pathPrefix}/${fileName}`;
    const storageRef = ref(storage, storagePath);

    // Set metadata if provided
    const uploadMetadata = {
        contentType: file.type,
        customMetadata: options.metadata || {},
    };

    // Start upload
    const uploadTask = uploadBytesResumable(storageRef, file, uploadMetadata);

    const uploadPromise = new Promise((resolve, reject) => {
        uploadTask.on(
            "state_changed",
            (snapshot) => {
                const progress = Math.round(
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                );
                if (options.onProgress) {
                    options.onProgress(progress);
                }
            },
            (error) => {
                reject(new Error(`Upload failed: ${error.message}`));
            },
            async () => {
                try {
                    const downloadURL = await getDownloadURL(
                        uploadTask.snapshot.ref
                    );
                    resolve(downloadURL);
                } catch (error) {
                    reject(
                        new Error(
                            `Failed to get download URL: ${error.message}`
                        )
                    );
                }
            }
        );
    });

    return uploadPromise.catch(async (error) => {
        await logError(id, "uploadFile", error, {
            pathPrefix: options.pathPrefix || "uploads",
        });
        throw error;
    });
};

export const uploadFighterProfileImage = async (file, fighterId) => {
    const breakDownFileName = file.name.split(".");
    const fileExtension =
        breakDownFileName[breakDownFileName.length - 1] ?? ".png";
    const storageRef = ref(
        storage,
        `fighterPictures/${fighterId}.${fileExtension}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);

    const uploadPromise = new Promise((resolve, reject) => {
        uploadTask.on(
            "state_changed",
            (snapshot) => {
                // Optional: you can track progress here if needed
                const progress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log(`Upload is ${progress}% done`);
            },
            (error) => {
                // Handle unsuccessful uploads
                reject(error);
            },
            async () => {
                try {
                    const downloadURL = await getDownloadURL(
                        uploadTask.snapshot.ref
                    );
                    const fighterRef = doc(fightersCollection, fighterId);

                    // updateAt too: the Partner API dates changes by it.
                    await updateDoc(fighterRef, {
                        fighterPicture: downloadURL,
                        updateAt: serverTimestamp(),
                    });
                    resolve(downloadURL);
                } catch (error) {
                    reject(error);
                }
            }
        );
    });

    return uploadPromise.catch(async (error) => {
        await logError(fighterId, "uploadFighterProfileImage", error);
        throw error;
    });
};

export const uploadRegisterFighterProfileImage = async (file, fighterId) => {
    const breakDownFileName = file.name.split(".");
    const fileExtension =
        breakDownFileName[breakDownFileName.length - 1] ?? ".png";
    const storageRef = ref(
        storage,
        `fighterRegister/${fighterId}.${fileExtension}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);

    const uploadPromise = new Promise((resolve, reject) => {
        uploadTask.on(
            "state_changed",
            (snapshot) => {
                // Optional: you can track progress here if needed
                const progress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log(`Upload is ${progress}% done`);
            },
            (error) => {
                // Handle unsuccessful uploads
                reject(error);
            },
            async () => {
                try {
                    const downloadURL = await getDownloadURL(
                        uploadTask.snapshot.ref
                    );
                    resolve(downloadURL);
                } catch (error) {
                    reject(error);
                }
            }
        );
    });

    return uploadPromise.catch(async (error) => {
        await logError(fighterId, "uploadRegisterFighterProfileImage", error);
        throw error;
    });
};

export const updateFighter = async (id, fighterData) => {
    if (!id) {
        return null;
    }

    const {
        name,
        email,
        nationality,
        birthdate,
        age,
        gender,
        phoneNumber,
        emergencyContactNumber,
        icOrPassport,
        gymNameOrTeamName,
        weightClass,
        height,
        records,
        walkOutMusic,
        igUsername,
        fighterPicture,
        tko,
        ko,
        submission,
        decision,
    } = fighterData;

    try {
        const fighterRef = doc(fightersCollection, id);

        await updateDoc(fighterRef, {
            name: name,
            email: email,
            nationality: nationality,
            birthdate: birthdate,
            age: age,
            gender: gender,
            phoneNumber: phoneNumber,
            emergencyContactNumber: emergencyContactNumber,
            icOrPassport: icOrPassport,
            gymNameOrTeamName: gymNameOrTeamName,
            weightClass: weightClass,
            height: height,
            records: records,
            walkOutMusic: walkOutMusic,
            igUsername: igUsername,
            fighterPicture: fighterPicture,
            tko: tko ?? 0,
            ko: ko ?? 0,
            submission: submission ?? 0,
            decision: decision ?? 0,
            updateAt: serverTimestamp(),
        });
        return fighterRef;
    } catch (error) {
        await logError(id, "updateFighter", error);
        return null;
    }
};

export const uploadEventCoverImage = async (file, eventId) => {
    const breakDownFileName = file.name.split(".");
    const fileExtension =
        breakDownFileName[breakDownFileName.length - 1] ?? ".png";
    const storageRef = ref(
        storage,
        `eventCoverImages/${eventId}.${fileExtension}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);

    const uploadPromise = new Promise((resolve, reject) => {
        uploadTask.on(
            "state_changed",
            (snapshot) => {
                // Optional: you can track progress here if needed
                const progress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log(`Upload is ${progress}% done`);
            },
            (error) => {
                // Handle unsuccessful uploads
                reject(error);
            },
            async () => {
                try {
                    const downloadURL = await getDownloadURL(
                        uploadTask.snapshot.ref
                    );
                    const eventRef = doc(eventsCollection, eventId);

                    // updateAt too: the Partner API dates changes by it.
                    await updateDoc(eventRef, {
                        coverImage: downloadURL,
                        updateAt: serverTimestamp(),
                    });
                    resolve(downloadURL);
                } catch (error) {
                    reject(error);
                }
            }
        );
    });

    return uploadPromise.catch(async (error) => {
        await logError(null, "uploadEventCoverImage", error, { eventId });
        throw error;
    });
};

export const createEvent = async (eventData) => {
    const {
        ticketUrl,
        coverImage,
        name,
        dateTime,
        status,
        registerExpiredDateTime,
        venue,
        address,
        googleMap,
        waze,
        details,
        color,
        liveStreamUrl,
        liveStreamTicketPurchaseDeadline,
        liveStreamTicketPrice,
        allowedEntries,
        isTest,
    } = eventData;

    try {
        const data = {
            ticketUrl: ticketUrl ?? null,
            coverImage: coverImage ?? null,
            name: name ?? null,
            dateTime: dateTime ?? null,
            status: status ?? null,
            registerExpiredDateTime: registerExpiredDateTime ?? null,
            venue: venue ?? null,
            address: address ?? null,
            googleMap: googleMap ?? null,
            waze: waze ?? null,
            details: details ?? null,
            color: color ?? null,
            liveStreamUrl: (() => {
                const urls = getLiveStreamUrls(liveStreamUrl);
                return urls.length > 0 ? urls : null;
            })(),
            liveStreamTicketPurchaseDeadline:
                liveStreamTicketPurchaseDeadline ?? null,
            liveStreamTicketPrice: liveStreamTicketPrice ?? null,
            allowedEntries: Array.isArray(allowedEntries)
                ? allowedEntries
                : null,
            isTest: isTest === true,
            isEnded: false,
            eventResults: [],
            endedAt: null,
            endedBy: null,
            createdAt: serverTimestamp(),
            updateAt: serverTimestamp(),
        };

        const docRef = await addDoc(eventsCollection, data);

        return {
            ...data,
            id: docRef?.id,
        };
    } catch (error) {
        await logError(null, "createEvent", error);
        return null;
    }
};

export const updateEvent = async (id, eventData) => {
    if (!id) {
        return null;
    }

    const {
        ticketUrl,
        coverImage,
        name,
        dateTime,
        status,
        registerExpiredDateTime,
        venue,
        address,
        googleMap,
        waze,
        details,
        color,
        liveStreamUrl,
        liveStreamTicketPurchaseDeadline,
        liveStreamTicketPrice,
        allowedEntries,
    } = eventData;

    try {
        const eventRef = doc(eventsCollection, id);

        await updateDoc(eventRef, {
            ticketUrl: ticketUrl,
            coverImage: coverImage,
            name: name,
            dateTime: dateTime ?? null,
            status: status,
            registerExpiredDateTime: registerExpiredDateTime ?? null,
            venue: venue,
            address: address,
            googleMap: googleMap,
            waze: waze,
            details: details,
            color: color,
            liveStreamUrl: (() => {
                const urls = getLiveStreamUrls(liveStreamUrl);
                return urls.length > 0 ? urls : null;
            })(),
            liveStreamTicketPurchaseDeadline:
                liveStreamTicketPurchaseDeadline ?? null,
            liveStreamTicketPrice: liveStreamTicketPrice,
            allowedEntries: Array.isArray(allowedEntries)
                ? allowedEntries
                : null,
            updateAt: serverTimestamp(),
        });
        return eventRef;
    } catch (error) {
        await logError(null, "updateEvent", error, { eventId: id });
        return null;
    }
};

// Saved on its own, straight away, rather than with the event form: the
// server blocks END EVENT, refunds and ticket sales from this flag, so it must
// never wait on an unsaved form.
export const setEventTestMode = async (id, isTest) => {
    await updateDoc(doc(eventsCollection, id), {
        isTest: isTest === true,
        updateAt: serverTimestamp(),
    });
};

// Partner Data API: hold round scores for this event until every active
// judge has submitted. Saved at once, like Test event.
export const setEventPartnerHoldScores = async (id, hold) => {
    await updateDoc(doc(eventsCollection, id), {
        partnerHoldScores: hold === true,
        updateAt: serverTimestamp(),
    });
};

const boutOrderError = (code, message, extra = {}) =>
    Object.assign(new Error(message), { code, ...extra });

const sameBoutOrder = (a, b) => {
    const x = Array.isArray(a) ? a : [];
    const y = Array.isArray(b) ? b : [];
    return x.length === y.length && x.every((k, i) => k === y[i]);
};

// updateEvent rewrites the whole form, so the running order gets its own write.
// It goes only while the stored order is still `expected`, the one the page
// last saw: Manage Event reads the event once, so without the check a tab
// left open would put back an order changed since on the results dashboard
// (the judges follow whatever is stored). Refused, it throws an Error with
// `code` STALE_ORDER (and the stored order on `boutOrder`), EVENT_ENDED or
// NO_EVENT.
export const setEventBoutOrder = async (id, boutOrder, expected) => {
    const eventRef = doc(eventsCollection, id);
    await runTransaction(db, async (tx) => {
        const snap = await tx.get(eventRef);
        if (!snap.exists()) {
            throw boutOrderError("NO_EVENT", "This event no longer exists.");
        }
        const stored = snap.data();
        if (stored.isEnded) {
            throw boutOrderError(
                "EVENT_ENDED",
                "The event has ended, so the running order can't change."
            );
        }
        if (!sameBoutOrder(stored.boutOrder, expected)) {
            throw boutOrderError(
                "STALE_ORDER",
                "The running order was changed on another screen.",
                { boutOrder: Array.isArray(stored.boutOrder) ? stored.boutOrder : [] }
            );
        }
        tx.update(eventRef, { boutOrder, updateAt: serverTimestamp() });
    });
};

export const matchEventFighter = async (eventFighterA, eventFighterB) => {
    try {
        const batch = writeBatch(db);

        const eventFighterARef = doc(eventFightersCollection, eventFighterA.id);
        const eventFighterBRef = doc(eventFightersCollection, eventFighterB.id);

        if (!!eventFighterA?.matchWith) {
            batch.update(
                doc(
                    eventFightersCollection,
                    `${eventFighterA?.eventId}_${eventFighterA.matchWith}`
                ),
                {
                    matchWith: null,
                    updateAt: serverTimestamp(),
                }
            );
        }

        if (!!eventFighterB?.matchWith) {
            batch.update(
                doc(
                    eventFightersCollection,
                    `${eventFighterB?.eventId}_${eventFighterB.matchWith}`
                ),
                {
                    matchWith: null,
                    updateAt: serverTimestamp(),
                }
            );
        }

        batch.update(eventFighterARef, {
            matchWith: eventFighterB.fighterId,
        });

        batch.update(eventFighterBRef, {
            matchWith: eventFighterA.fighterId,
        });

        await batch.commit();
    } catch (error) {
        await logError(null, "matchEventFighter", error, {
            eventFighterAId: eventFighterA?.id,
            eventFighterBId: eventFighterB?.id,
        });
        throw error;
    }
};

export const unMatchEventFighter = async (eventFighterA, eventFighterB) => {
    try {
        const batch = writeBatch(db);

        const eventFighterARef = doc(eventFightersCollection, eventFighterA.id);
        const eventFighterBRef = doc(eventFightersCollection, eventFighterB.id);

        batch.update(eventFighterARef, {
            matchWith: null,
            updateAt: serverTimestamp(),
        });

        batch.update(eventFighterBRef, {
            matchWith: null,
            updateAt: serverTimestamp(),
        });

        await batch.commit();

        return true;
    } catch (error) {
        await logError(null, "unMatchEventFighter", error, {
            eventFighterAId: eventFighterA?.id,
            eventFighterBId: eventFighterB?.id,
        });
        throw error;
    }
};

export const getRanking = async () => {
    try {
        const q = query(
            rankingsCollection,
            orderBy("createdAt", "desc"),
            limit(1)
        );

        const snapshot = await getDocs(q);

        if (snapshot.size === 1) {
            return {
                ...snapshot.docs[0].data(),
                id: snapshot.docs[0].id,
            };
        }
    } catch (error) {
        await logError(null, "getRanking", error);
        return {};
    }
};

export const fetchEventFighters = httpsCallable(functions, "getEventFighters");
export const fetchFighter = httpsCallable(functions, "getFighter");
export const fetchEvent = httpsCallable(functions, "getEvent");
export const fetchActiveEvents = httpsCallable(functions, "getActiveEvents");
export const fetchLiveStream = httpsCallable(functions, "verifyLiveStream");
export const generatePaymentLink = httpsCallable(
    functions,
    "createPaymentLink"
);

export const getLiveStream = async (eventId, code) => {
    try {
        if (!eventId || !code) {
            console.error("Missing required parameters: eventId or code");
            return null;
        }

        const fetchedLiveStream = await fetchLiveStream({
            eventId: eventId,
            code: code,
        });

        if (!fetchedLiveStream || !fetchedLiveStream.data) {
            console.error("Invalid response from getLiveStream function");
            return null;
        }

        return fetchedLiveStream.data;
    } catch (error) {
        await logError(null, "getLiveStream", error, { eventId, code });

        // Handle specific error types
        if (error.code === "functions/unavailable") {
            console.error("Firebase Functions service is unavailable");
        } else if (error.code === "functions/unauthenticated") {
            console.error("User is not authenticated");
        } else if (error.code === "functions/permission-denied") {
            console.error("Permission denied to access live stream");
        } else if (error.message && error.message.includes("CORS")) {
            console.error("CORS error - function may not be properly deployed");
        }

        return null;
    }
};

export const getEvents = async () => {
    try {
        const fetchedEvents = await fetchActiveEvents();
        return [
            ...fetchedEvents.data.filter((e) => e.isTest !== true).map((e) => {
                return {
                    ...e,
                    dateTime: !e.dateTime?._seconds
                        ? null
                        : Timestamp.fromMillis(e.dateTime?._seconds * 1000),
                    registerExpiredDateTime: !e.registerExpiredDateTime
                        ?._seconds
                        ? null
                        : Timestamp.fromMillis(
                              e.registerExpiredDateTime?._seconds * 1000
                          ),
                    liveStreamTicketPurchaseDeadline: !e
                        .liveStreamTicketPurchaseDeadline?._seconds
                        ? null
                        : Timestamp.fromMillis(
                              e.liveStreamTicketPurchaseDeadline?._seconds *
                                  1000
                          ),
                    createdAt: !e.createdAt?._seconds
                        ? null
                        : Timestamp.fromMillis(e.createdAt?._seconds * 1000),
                    updateAt: !e.updateAt?._seconds
                        ? null
                        : Timestamp.fromMillis(e.updateAt?._seconds * 1000),
                };
            }),
        ];
    } catch (error) {
        await logError(null, "getEvents", error);
        return [];
    }
};

export const getLiveStreamTickets = async ({
    size = 10,
    lastDoc = null,
    order = "createdAt",
    sort = "desc",
}) => {
    try {
        let q = query(liveStreamTicketsCollection, orderBy(order, sort));

        if (!!lastDoc) {
            q = query(
                liveStreamTicketsCollection,
                orderBy(order, sort),
                startAfter(lastDoc)
            );
        }

        const querySnapshot = await getDocs(q);

        const data =
            querySnapshot.size < 1
                ? []
                : querySnapshot.docs.map((doc) => ({
                      id: doc.id,
                      ...doc.data(),
                  }));

        const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
        return { data, lastVisible };
    } catch (error) {
        await logError(null, "getLiveStreamTickets", error);
        return null;
    }
};

export const registerFighter = async (currentUser, formData) => {
    const {
        name,
        nationality,
        email,
        birthdate,
        age,
        gender,
        phoneNumber,
        emergencyContactNumber,
        records,
        icOrPassport,
        gymNameOrTeamName,
        weightClass,
        height,
        walkOutMusic,
        igUsername,
        fighterPicture,
    } = formData;

    // Use auth.currentUser.uid as fallback if currentUser.uid is undefined
    // This can happen when a new user is redirected immediately after signup
    const uid = currentUser?.uid || auth.currentUser?.uid;

    if (!uid) {
        throw new Error(
            "User ID is not available. Please ensure you are logged in."
        );
    }

    if (!fighterPicture) {
        throw new Error("Missing Fighter Picture or Receipt.");
    }

    try {
        const registerFighterPictureUrl = await uploadFile(
            fighterPicture,
            uid,
            {
                pathPrefix: "registerFighterPicture",
            }
        );

        const docRef = await addDoc(fighterRequestsCollection, {
            uid: uid,
            name: name,
            email: email,
            phoneNumber: phoneNumber,
            authIdentifier: currentUser?.authIdentifier ?? "",
            type: fighterRequestType.newRegister,
            status: fighterRequestStatus.pending,
            data: {
                fighterPicture: registerFighterPictureUrl,
                email: email,
                name: name,
                gender: gender,
                weightClass: weightClass,
                height: height,
                birthdate: birthdate,
                age: age,
                nationality: nationality,
                phoneNumber: phoneNumber,
                emergencyContactNumber: emergencyContactNumber,
                icOrPassport: icOrPassport,
                igUsername: igUsername,
                gymNameOrTeamName: gymNameOrTeamName,
                walkOutMusic: walkOutMusic,
                records: records,
            },
            comments: [],
            createdAt: serverTimestamp(),
            updateAt: serverTimestamp(),
        });

        // Wait a moment for auto-approval trigger to process (if enabled)
        // Then check if request was auto-approved
        // await new Promise((resolve) => setTimeout(resolve, 2000));

        // try {
        //     const requestDoc = await getDoc(docRef);
        //     if (requestDoc.exists()) {
        //         const requestData = requestDoc.data();
        //         return {
        //             requestId: docRef.id,
        //             status: requestData.status,
        //             autoApproved:
        //                 requestData.status === fighterRequestStatus.approved,
        //         };
        //     }
        // } catch (error) {
        //     console.warn("Could not check request status after creation:", error);
        // }

        return {
            requestId: docRef.id,
            status: fighterRequestStatus.pending,
            autoApproved: true, //TODO change to false when auto-approval is disabled
        };
    } catch (error) {
        await logError(uid, "registerFighter", error);
        throw error;
    }
};
export const updateRegisterFighter = async (id, formData) => {
    const {
        uid,
        name,
        nationality,
        email,
        birthdate,
        age,
        gender,
        phoneNumber,
        emergencyContactNumber,
        records,
        icOrPassport,
        gymNameOrTeamName,
        weightClass,
        height,
        walkOutMusic,
        igUsername,
        fighterPicture,
    } = formData;

    try {
        let registerFighterPictureUrl;

        if (fighterPicture instanceof File) {
            registerFighterPictureUrl = await uploadFile(fighterPicture, uid, {
                pathPrefix: "registerFighterPicture",
            });
        } else {
            registerFighterPictureUrl = fighterPicture;
        }

        const ref = doc(fighterRequestsCollection, id);

        await updateDoc(ref, {
            status: fighterRequestStatus.pending,
            data: {
                name: name,
                nationality: nationality,
                birthdate: birthdate,
                age: age,
                gender: gender,
                phoneNumber: phoneNumber,
                emergencyContactNumber: emergencyContactNumber,
                records: records,
                icOrPassport: icOrPassport,
                gymNameOrTeamName: gymNameOrTeamName,
                weightClass: weightClass,
                height: height,
                walkOutMusic: walkOutMusic,
                igUsername: igUsername,
                fighterPicture: registerFighterPictureUrl,
            },
            updateAt: serverTimestamp(),
        });

        return true;
    } catch (error) {
        await logError(uid, "updateRegisterFighter", error, { requestId: id });
        throw error;
    }
};

export const addComment = async (requestId, commentText, createdBy) => {
    try {
        const ref = doc(fighterRequestsCollection, requestId);

        await updateDoc(ref, {
            comments: arrayUnion({
                text: commentText,
                createdAt: Timestamp.now(),
                createdBy: createdBy,
            }),
            updateAt: serverTimestamp(),
        });
    } catch (error) {
        await logError(createdBy, "addComment", error, {
            requestId,
        });
        throw error;
    }
};

export const updateFighterRequestStatus = async (fighterRequestId, status) => {
    try {
        const ref = doc(fighterRequestsCollection, fighterRequestId);

        await updateDoc(ref, {
            status: status,
            updateAt: serverTimestamp(),
        });
    } catch (error) {
        await logError(null, "updateFighterRequestStatus", error, {
            fighterRequestId,
            status,
        });
        throw error;
    }
};

export const registerEvent = async (
    currentUser,
    eventId,
    fighterId,
    email,
    entry,
    eventWeightClass,
    receipt,
    options = {}
) => {
    try {
        const method = options.paymentMethod || paymentMethod.slip;
        const tokenId = options.tokenId || null;

        let receiptUrl = null;
        if (method === paymentMethod.slip) {
            if (receipt instanceof File) {
                receiptUrl = await uploadFile(
                    receipt,
                    `${eventId}_${fighterId}`,
                    {
                        pathPrefix: "registerReceipt",
                    }
                );
            } else {
                receiptUrl = receipt;
            }
        }

        let authIdentifier = currentUser?.authIdentifier ?? "";
        if (!authIdentifier) {
            try {
                const userSnap = await getDoc(doc(db, "users", fighterId));
                authIdentifier = userSnap.exists()
                    ? userSnap.data()?.authIdentifier ?? ""
                    : "";
            } catch {
                authIdentifier = "";
            }
        }

        await addDoc(fighterRequestsCollection, {
            uid: fighterId,
            name: currentUser?.fighter?.name ?? "",
            eventId: eventId,
            email: currentUser?.email ?? "",
            phoneNumber: currentUser?.phoneNumber ?? "",
            authIdentifier: authIdentifier,
            type: fighterRequestType.registerEvent,
            status: fighterRequestStatus.pending,
            data: {
                fighterId: fighterId,
                eventId: eventId,
                entry: entry,
                eventWeightClass: eventWeightClass,
                receipt: receiptUrl,
                paymentMethod: method,
                tokenId: tokenId,
            },
            createdAt: serverTimestamp(),
            updateAt: serverTimestamp(),
        });
        return;
    } catch (error) {
        await logError(fighterId, "registerEvent", error, {
            eventId,
            receiptType: receipt instanceof File ? "File" : typeof receipt,
            hasEntry: !!entry,
            hasWeightClass: !!eventWeightClass,
        });
        throw error;
    }
};
export const updateRegisteredEvent = async (
    requestId,
    eventId,
    fighterId,
    entry,
    eventWeightClass,
    receipt,
    options = {}
) => {
    try {
        const method = options.paymentMethod || paymentMethod.slip;
        const tokenId = options.tokenId || null;

        let receiptUrl = null;
        if (method === paymentMethod.slip) {
            if (receipt instanceof File) {
                receiptUrl = await uploadFile(
                    receipt,
                    `${eventId}_${fighterId}`,
                    {
                        pathPrefix: "registerReceipt",
                    }
                );
            } else {
                receiptUrl = receipt;
            }
        }
        const requestRef = doc(fighterRequestsCollection, requestId);

        await updateDoc(requestRef, {
            status: fighterRequestStatus.pending,
            data: {
                fighterId: fighterId,
                eventId: eventId,
                entry: entry,
                eventWeightClass: eventWeightClass,
                receipt: method === paymentMethod.slip ? receiptUrl ?? receipt : null,
                paymentMethod: method,
                tokenId: tokenId,
            },
            updateAt: serverTimestamp(),
        });
    } catch (error) {
        await logError(fighterId, "updateRegisteredEvent", error, {
            eventId,
            requestId,
            receiptType: receipt instanceof File ? "File" : typeof receipt,
            hasEntry: !!entry,
            hasWeightClass: !!eventWeightClass,
        });
        throw error;
    }
};

/**
 * A registered fighter declaring a new weight for an event they are already in.
 *
 * This rides the existing fighter-request rails (PENDING → APPROVED/REJECTED,
 * comments, admin queue, emails) rather than writing the registration directly:
 * an already-registered fighter changing class can invalidate a booked bout, so
 * it needs an admin's eyes.
 *
 * Re-declaring before the last request is decided updates that request in place
 * instead of stacking a second one on the queue.
 *
 * @param {object} currentUser
 * @param {string} eventId
 * @param {string} eventWeightClass the newly declared weight class (A–K)
 * @param {{previousWeightClass?: string|null, eventName?: string}} [meta]
 */
export const requestWeightChange = async (
    currentUser,
    eventId,
    eventWeightClass,
    meta = {}
) => {
    const fighterId = currentUser?.uid;
    try {
        if (!fighterId) throw new Error("Not signed in.");
        if (!eventId) throw new Error("Missing event.");
        if (!eventWeightClass) throw new Error("Pick a weight class first.");

        const data = {
            fighterId,
            eventId,
            eventWeightClass,
            previousWeightClass: meta.previousWeightClass ?? null,
            weightDeclaredAt: Timestamp.now(),
        };

        // Fold into a still-pending request rather than queueing another.
        const pending = await getDocs(
            query(
                fighterRequestsCollection,
                where("uid", "==", fighterId),
                where("eventId", "==", eventId),
                where("type", "==", fighterRequestType.weightChange),
                where("status", "==", fighterRequestStatus.pending),
                limit(1)
            )
        );
        if (!pending.empty) {
            await updateDoc(pending.docs[0].ref, {
                data,
                updateAt: serverTimestamp(),
            });
            return;
        }

        let authIdentifier = currentUser?.authIdentifier ?? "";
        if (!authIdentifier) {
            try {
                const userSnap = await getDoc(doc(db, "users", fighterId));
                authIdentifier = userSnap.exists()
                    ? userSnap.data()?.authIdentifier ?? ""
                    : "";
            } catch {
                authIdentifier = "";
            }
        }

        await addDoc(fighterRequestsCollection, {
            uid: fighterId,
            name: currentUser?.fighter?.name ?? "",
            eventId,
            email: currentUser?.email ?? "",
            phoneNumber: currentUser?.phoneNumber ?? "",
            authIdentifier,
            type: fighterRequestType.weightChange,
            status: fighterRequestStatus.pending,
            data,
            createdAt: serverTimestamp(),
            updateAt: serverTimestamp(),
        });
    } catch (error) {
        await logError(fighterId, "requestWeightChange", error, {
            eventId,
            eventWeightClass,
        });
        throw error;
    }
};

/**
 * A fighter's live registrations — the ones an admin weight edit could touch.
 * Excludes ended events and pulled-out registrations, since changing the weight
 * on those achieves nothing.
 *
 * @param {string} fighterId
 * @returns {Promise<Array>} `{ id, eventId, eventName, eventDate, entry,
 *   eventWeightClass, matchWith, opponentName }`
 */
export const fetchLiveRegistrationsForFighter = async (fighterId) => {
    try {
        if (!fighterId) return [];
        const snap = await getDocs(
            query(eventFightersCollection, where("fighterId", "==", fighterId))
        );

        const rows = await Promise.all(
            snap.docs.map(async (d) => {
                const ef = d.data();
                if (ef?.isPulledOut) return null;

                const eventSnap = await getDoc(
                    doc(eventsCollection, ef.eventId)
                );
                if (!eventSnap.exists()) return null;
                const event = eventSnap.data();
                if (event?.isEnded) return null;

                let opponentName = null;
                if (ef.matchWith) {
                    const oppSnap = await getDoc(
                        doc(fightersCollection, ef.matchWith)
                    );
                    opponentName = oppSnap.exists()
                        ? oppSnap.data()?.name ?? null
                        : null;
                }

                return {
                    id: d.id,
                    eventId: ef.eventId,
                    eventName: event?.name ?? "Untitled event",
                    eventDate: event?.dateTime ?? null,
                    entry: ef.entry ?? null,
                    eventWeightClass: ef.eventWeightClass ?? null,
                    createdAt: ef.createdAt ?? null,
                    matchWith: ef.matchWith ?? null,
                    opponentName,
                };
            })
        );

        return rows.filter(Boolean);
    } catch (error) {
        await logError(fighterId, "fetchLiveRegistrationsForFighter", error);
        return [];
    }
};

/**
 * Push a weight class onto specific registrations.
 *
 * Deliberately narrow: it writes the weight class and nothing else. A matched
 * registration keeps its pairing — the bout is marked
 * `needsRematchReview` instead, because silently unmatching two people behind
 * the admin's back is never the right call.
 *
 * @param {string} fighterId
 * @param {Array<string>} eventFighterIds ids from fetchLiveRegistrationsForFighter
 * @param {string} weightClass the class to write (A–K)
 */
export const applyWeightToRegistrations = async (
    fighterId,
    eventFighterIds,
    weightClass
) => {
    try {
        const ids = Array.isArray(eventFighterIds) ? eventFighterIds : [];
        if (ids.length === 0) return 0;
        if (!weightClass) throw new Error("Missing weight class.");

        await Promise.all(
            ids.map(async (efId) => {
                const ref = doc(eventFightersCollection, efId);
                const snap = await getDoc(ref);
                if (!snap.exists()) return;
                const ef = snap.data();

                const update = {
                    eventWeightClass: weightClass,
                    weightDeclaredAt: Timestamp.now(),
                    updateAt: serverTimestamp(),
                };
                // Booked bout + a class move = the matchmaker needs to look again.
                if (ef?.matchWith && ef?.eventWeightClass !== weightClass) {
                    update.needsRematchReview = true;
                }
                await updateDoc(ref, update);
            })
        );
        return ids.length;
    } catch (error) {
        await logError(fighterId, "applyWeightToRegistrations", error, {
            count: eventFighterIds?.length,
            weightClass,
        });
        throw error;
    }
};

export const fetchAgreementForEvent = async (uid, eventId) => {
    try {
        if (!uid || !eventId) return null;
        const ref = doc(agreementsCollection, `${uid}_${eventId}`);
        const snap = await getDoc(ref);
        return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    } catch (error) {
        await logError(uid, "fetchAgreementForEvent", error, { eventId });
        return null;
    }
};

export const recordAgreement = async (uid, eventId) => {
    try {
        const result = await recordAgreementCallable({
            eventId,
            userAgent: navigator.userAgent,
            agreementVersion: AGREEMENT_VERSION,
        });
        return result?.data ?? null;
    } catch (error) {
        await logError(uid, "recordAgreement", error, { eventId });
        throw error;
    }
};

export const fetchActiveTokensForFighter = async (fighterId) => {
    try {
        if (!fighterId) return [];
        const q = query(
            fighterTokensCollection,
            where("fighterId", "==", fighterId),
            where("status", "==", tokenStatus.active)
        );
        const snap = await getDocs(q);
        return snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => {
                const aMs = a.issuedAt?.toMillis?.() ?? 0;
                const bMs = b.issuedAt?.toMillis?.() ?? 0;
                return aMs - bMs;
            });
    } catch (error) {
        await logError(fighterId, "fetchActiveTokensForFighter", error);
        return [];
    }
};

export const fetchAllTokensForFighter = async (fighterId) => {
    try {
        if (!fighterId) return [];
        const q = query(
            fighterTokensCollection,
            where("fighterId", "==", fighterId)
        );
        const snap = await getDocs(q);
        return snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => {
                const aMs = a.issuedAt?.toMillis?.() ?? 0;
                const bMs = b.issuedAt?.toMillis?.() ?? 0;
                return bMs - aMs;
            });
    } catch (error) {
        await logError(fighterId, "fetchAllTokensForFighter", error);
        return [];
    }
};

export const fetchActiveTokenCountsForFighters = async (fighterIds) => {
    try {
        const ids = Array.from(new Set((fighterIds ?? []).filter(Boolean)));
        if (ids.length === 0) return {};
        const counts = {};
        ids.forEach((id) => {
            counts[id] = 0;
        });
        for (let i = 0; i < ids.length; i += 10) {
            const chunk = ids.slice(i, i + 10);
            const q = query(
                fighterTokensCollection,
                where("fighterId", "in", chunk),
                where("status", "==", tokenStatus.active)
            );
            const snap = await getDocs(q);
            snap.docs.forEach((d) => {
                const fid = d.data()?.fighterId;
                if (!fid) return;
                counts[fid] = (counts[fid] ?? 0) + 1;
            });
        }
        return counts;
    } catch (error) {
        await logError(null, "fetchActiveTokenCountsForFighters", error);
        return {};
    }
};

export const issueFighterToken = async ({ fighterId, sourceEventId }) => {
    try {
        const result = await issueFighterTokenCallable({
            fighterId,
            sourceEventId,
        });
        return result?.data ?? null;
    } catch (error) {
        await logError(null, "issueFighterToken", error, {
            fighterId,
            sourceEventId,
        });
        throw error;
    }
};

export const logError = async (userId, action, error, context = {}) => {
    try {
        await addDoc(errorLogsCollection, {
            userId: userId ?? null,
            action,
            errorMessage: error?.message ?? String(error),
            errorCode: error?.code ?? null,
            timestamp: serverTimestamp(),
            userAgent: navigator.userAgent,
            url: window.location.href,
            ...context,
        });
    } catch (err) {
        console.error("Failed to log error:", err);
    }
};

export const requestUpdateFighter = async (
    requestId,
    formData,
    currentUser
) => {
    try {
        let registerFighterPictureUrl;

        if (formData.fighterPicture instanceof File) {
            registerFighterPictureUrl = await uploadFile(
                formData.fighterPicture,
                currentUser.uid,
                {
                    pathPrefix: "updateInfoFighterPicture",
                }
            );
        } else {
            registerFighterPictureUrl = formData.fighterPicture;
        }

        const docData = {
            uid: currentUser.uid,
            name: currentUser.fighter.name,
            email: currentUser.email,
            phoneNumber: currentUser.phoneNumber,
            authIdentifier: currentUser.authIdentifier,
            type: fighterRequestType.updateInfo,
            status: fighterRequestStatus.pending,
            updateAt: serverTimestamp(),
            data: {
                ...formData,
                fighterPicture: registerFighterPictureUrl,
            },
        };
        if (!!requestId) {
            const ref = doc(fighterRequestsCollection, requestId);
            await setDoc(ref, { ...docData }, { merge: true });
        } else {
            await addDoc(fighterRequestsCollection, {
                ...docData,
                createdAt: serverTimestamp(),
                comments: [],
            });
        }
    } catch (error) {
        await logError(currentUser.uid, "requestUpdateFighter", error, {
            requestId,
        });
        throw error;
    }
};

// ── Judge access (admin side) ────────────────────────────────────────────
// One link per event (event-judge-links/{eventId}) plus one doc per judge
// (event-judges/{judgeId}). judgeSignIn (functions) sets `lastRedeemedAt`
// on the judge it signs in, and every call from the judge page refreshes
// `lastSeenAt` (at most once a minute): the Judges tab's LIVE / AWAY.
//
// PIN generation reads the event's current judges first and picks a PIN at
// least 3 digits away from all of them. Two admins clicking at the same
// moment could each read the same list and pick PINs that are too close.
// That race is accepted: judging is set up by one admin, and a New PIN click
// fixes it.

const byCreatedAt = (a, b) =>
    (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0);

// Judge timestamps are the server's (serverTimestamp): the Partner API
// orders judges and dates judging changes by them, so an admin's clock
// mustn't matter. Until a write lands, read them as the local estimate.
const judgeData = (d) => ({ id: d.id, ...d.data({ serverTimestamps: "estimate" }) });

// Equality on eventId only, so no composite index; ordered client-side.
const readEventJudges = async (eventId) => {
    const snap = await getDocs(
        query(eventJudgesCollection, where("eventId", "==", eventId))
    );
    return snap.docs.map(judgeData).sort(byCreatedAt);
};

const newJudgeDoc = (eventId, name, pin, now) => ({
    eventId,
    name,
    // Plain text on purpose, and readable only by admins (firestore.rules).
    // A 4-digit PIN has 10,000 possible values, so a hash would be brute-
    // forced instantly and protect nothing, while stopping admins from
    // reading PINs out to judges. Don't "fix" this into a hash; keep the
    // collection locked down instead.
    pin,
    createdAt: now,
    updatedAt: now,
    revokedAt: null,
    lastRedeemedAt: null,
    lastSeenAt: null,
});

export const fetchEventJudges = async (eventId) => {
    try {
        if (!eventId) return [];
        return await readEventJudges(eventId);
    } catch (error) {
        await logError(null, "fetchEventJudges", error, { eventId });
        throw error;
    }
};

export const fetchJudgeLink = async (eventId) => {
    try {
        if (!eventId) return null;
        const snap = await getDoc(doc(eventJudgeLinksCollection, eventId));
        return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    } catch (error) {
        await logError(null, "fetchJudgeLink", error, { eventId });
        throw error;
    }
};

/**
 * First-time setup: creates the event's judge link and `count` judges named
 * "Judge 1".."Judge N", each with a valid PIN, in one batch.
 * @returns {Promise<{link: object, judges: Array<object>}>}
 */
export const setupEventJudging = async (eventId, count = 3) => {
    try {
        if (!eventId) throw new Error("Missing event id.");
        const linkRef = doc(eventJudgeLinksCollection, eventId);
        const linkSnap = await getDoc(linkRef);
        if (linkSnap.exists()) {
            throw new Error("Judging is already set up for this event.");
        }

        const existing = await readEventJudges(eventId);
        const pins = existing.map((j) => j.pin);
        const names = existing.map((j) => j.name);
        const now = serverTimestamp();
        const batch = writeBatch(db);

        const link = {
            eventId,
            token: generateJudgeToken(),
            createdAt: now,
            rotatedAt: null,
        };
        batch.set(linkRef, link);

        const created = [];
        for (let i = 0; i < count; i += 1) {
            const name = nextJudgeName(names);
            const pin = generateJudgePin(pins);
            names.push(name);
            pins.push(pin);
            const ref = doc(eventJudgesCollection);
            const data = newJudgeDoc(eventId, name, pin, now);
            batch.set(ref, data);
            created.push({ id: ref.id, ...data });
        }

        await batch.commit();
        return {
            link: { id: eventId, ...link },
            judges: [...existing, ...created],
        };
    } catch (error) {
        await logError(null, "setupEventJudging", error, { eventId, count });
        throw error;
    }
};

/**
 * Replaces the event's link token. Every QR / URL handed out before stops
 * working, so the UI confirms first.
 */
export const rotateJudgeLink = async (eventId) => {
    try {
        if (!eventId) throw new Error("Missing event id.");
        const update = {
            token: generateJudgeToken(),
            rotatedAt: serverTimestamp(),
            // A new token also lifts a wrong-PIN lockout on the old one.
            lockedUntil: null,
        };
        await updateDoc(doc(eventJudgeLinksCollection, eventId), update);
        return update;
    } catch (error) {
        await logError(null, "rotateJudgeLink", error, { eventId });
        throw error;
    }
};

/** Adds the next free "Judge N" with a fresh PIN. */
export const addEventJudge = async (eventId) => {
    try {
        if (!eventId) throw new Error("Missing event id.");
        const judges = await readEventJudges(eventId);
        const name = nextJudgeName(judges.map((j) => j.name));
        // Revoked judges' PINs count too: they can be restored.
        const pin = generateJudgePin(judges.map((j) => j.pin));
        const ref = doc(eventJudgesCollection);
        const data = newJudgeDoc(eventId, name, pin, serverTimestamp());
        await setDoc(ref, data);
        return { id: ref.id, ...data };
    } catch (error) {
        await logError(null, "addEventJudge", error, { eventId });
        throw error;
    }
};

/**
 * Gives one judge a new PIN, at least 3 digits away from every PIN on the
 * event, including this judge's old one (so it is a real change).
 */
export const regenerateJudgePin = async (eventId, judgeId) => {
    try {
        if (!eventId || !judgeId) throw new Error("Missing event or judge id.");
        const judges = await readEventJudges(eventId);
        const pin = generateJudgePin(judges.map((j) => j.pin));
        const update = { pin, updatedAt: serverTimestamp() };
        await updateDoc(doc(eventJudgesCollection, judgeId), update);
        return update;
    } catch (error) {
        await logError(null, "regenerateJudgePin", error, { eventId, judgeId });
        throw error;
    }
};

export const JUDGE_NAME_MAX_LENGTH = 40;

/**
 * Renames one judge. The name is what the judge sees after signing in and
 * what labels their card on the Matched tab, so two judges on the same event
 * may not share one (compared case-insensitively).
 * @returns {Promise<{name: string}>}
 */
export const renameEventJudge = async (eventId, judgeId, rawName) => {
    try {
        if (!eventId || !judgeId) throw new Error("Missing event or judge id.");
        const name = String(rawName ?? "").replace(/\s+/g, " ").trim();
        if (!name) throw new Error("Enter a name for the judge.");
        if (name.length > JUDGE_NAME_MAX_LENGTH) {
            throw new Error(
                `Keep the name under ${JUDGE_NAME_MAX_LENGTH + 1} characters.`
            );
        }
        const others = await readEventJudges(eventId);
        const clash = others.find(
            (j) =>
                j.id !== judgeId &&
                String(j.name ?? "").trim().toLowerCase() === name.toLowerCase()
        );
        if (clash) throw new Error(`Another judge is already called "${name}".`);
        await updateDoc(doc(eventJudgesCollection, judgeId), {
            name,
            updatedAt: serverTimestamp(),
        });
        return { name };
    } catch (error) {
        await logError(null, "renameEventJudge", error, { eventId, judgeId });
        throw error;
    }
};

/**
 * Deletes a judge who has never scored anything on this event (not even a
 * draft round). Anyone with a card is revoked instead, so their scores keep
 * a name. Their PIN stops working at once: the judge callables re-read the
 * judge on every call.
 */
export const removeEventJudge = async (eventId, judgeId) => {
    try {
        if (!eventId || !judgeId) throw new Error("Missing event or judge id.");
        const ref = doc(eventJudgesCollection, judgeId);
        const snap = await getDoc(ref);
        if (!snap.exists() || snap.data().eventId !== eventId) {
            throw new Error("That judge is no longer on this event.");
        }
        const cards = await getDocs(
            query(
                judgeScorecardsCollection,
                where("eventId", "==", eventId),
                where("judgeId", "==", judgeId),
                limit(1)
            )
        );
        if (!cards.empty) {
            throw new Error(
                `${snap.data().name || "This judge"} has already scored, so they can only be revoked.`
            );
        }
        await deleteDoc(ref);
        return { name: snap.data().name };
    } catch (error) {
        await logError(null, "removeEventJudge", error, { eventId, judgeId });
        throw error;
    }
};

/** Revokes (revoked = true) or restores (false) one judge. */
export const setJudgeRevoked = async (judgeId, revoked) => {
    try {
        if (!judgeId) throw new Error("Missing judge id.");
        const now = serverTimestamp();
        const update = { revokedAt: revoked ? now : null, updatedAt: now };
        await updateDoc(doc(eventJudgesCollection, judgeId), update);
        return update;
    } catch (error) {
        await logError(null, "setJudgeRevoked", error, { judgeId, revoked });
        throw error;
    }
};

// ── Live judging data (admin) ───────────────────────────────────────────
// Each returns the unsubscribe function, for a useEffect cleanup.

export const subscribeEventJudges = (eventId, onData, onError) =>
    onSnapshot(
        query(eventJudgesCollection, where("eventId", "==", eventId)),
        (snap) =>
            onData(
                snap.docs.map(judgeData).sort(byCreatedAt)
            ),
        (error) => {
            logError(null, "subscribeEventJudges", error, { eventId });
            onError?.(error);
        }
    );

export const subscribeJudgeLink = (eventId, onData, onError) =>
    onSnapshot(
        doc(eventJudgeLinksCollection, eventId),
        (snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
        (error) => {
            logError(null, "subscribeJudgeLink", error, { eventId });
            onError?.(error);
        }
    );

export const subscribeEventScorecards = (eventId, onData, onError) =>
    onSnapshot(
        query(judgeScorecardsCollection, where("eventId", "==", eventId)),
        (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        (error) => {
            logError(null, "subscribeEventScorecards", error, { eventId });
            onError?.(error);
        }
    );

// ── Bout corners (admin) ────────────────────────────────────────────────
// Red (left) / blue (right) per bout lives in events/{id}.boutCorners. Swaps
// go through a callable because the server must refuse once any judge has a
// card for the bout, which rules can't check.
const swapBoutCornersCallable = httpsCallable(functions, "swapBoutCorners");

/** Resolves to the callable's { success, code, error, redFighterId }. */
export const swapBoutCorners = async (eventId, pairKey) => {
    try {
        return (await swapBoutCornersCallable({ eventId, pairKey })).data;
    } catch (error) {
        await logError(null, "swapBoutCorners", error, { eventId, pairKey });
        throw error;
    }
};

// ── Round unlock (admin) ────────────────────────────────────────────────
// A judge's round locks when they press NEXT ROUND. Only an admin can unlock
// one, with a reason; a submitted card reopens for the judge to re-submit.
const unlockJudgeRoundCallable = httpsCallable(functions, "unlockJudgeRound");

/** Resolves to the callable's { success, code, error, reopened }. */
export const unlockJudgeRound = async (payload) => {
    try {
        return (await unlockJudgeRoundCallable(payload)).data;
    } catch (error) {
        await logError(null, "unlockJudgeRound", error, {
            eventId: payload?.eventId,
            boutKey: payload?.boutKey,
            round: payload?.round,
        });
        throw error;
    }
};

// ── Partner API keys (admin) ────────────────────────────────────────────
const createPartnerApiKeyCallable = httpsCallable(functions, "createPartnerApiKey");
const revokePartnerApiKeyCallable = httpsCallable(functions, "revokePartnerApiKey");
const deletePartnerApiKeyCallable = httpsCallable(functions, "deletePartnerApiKey");

const partnerKeyCall = (callable, action) => async (payload) => {
    try {
        return (await callable(payload)).data;
    } catch (error) {
        await logError(null, action, error, { id: payload?.id ?? null });
        throw error;
    }
};

/** { name, expiresAt? } → { success, id, prefix, key } — the key is shown once. */
export const createPartnerApiKey = partnerKeyCall(createPartnerApiKeyCallable, "createPartnerApiKey");
/** { id } → { success } */
export const revokePartnerApiKey = partnerKeyCall(revokePartnerApiKeyCallable, "revokePartnerApiKey");
/** { id } → { success }; revoked keys only. */
export const deletePartnerApiKey = partnerKeyCall(deletePartnerApiKeyCallable, "deletePartnerApiKey");

/** Where the partnerApi function answers, for the admin page's copy box.
 * Same region as the function (functions/index.js). */
const PARTNER_API_REGION = "asia-southeast1";
export const partnerApiBaseUrl = USE_EMULATOR
    ? `http://127.0.0.1:5001/${firebaseConfig.projectId}/${PARTNER_API_REGION}/partnerApi`
    : `https://${PARTNER_API_REGION}-${firebaseConfig.projectId}.cloudfunctions.net/partnerApi`;

// ── Judge page (/judge/:token) ──────────────────────────────────────────
// Judges aren't signed in to Firebase Auth, so these don't logError (the
// error-logs rule needs an authenticated user). Each resolves to the
// callable's { success, code, error, ... } payload.

const judgeSignInCallable = httpsCallable(functions, "judgeSignIn");
const judgeGetEventCallable = httpsCallable(functions, "judgeGetEvent");
const judgeSaveCardCallable = httpsCallable(functions, "judgeSaveCard");

export const judgeSignIn = async (payload) =>
    (await judgeSignInCallable(payload)).data;
export const judgeGetEvent = async (session) =>
    (await judgeGetEventCallable(session)).data;
export const judgeSaveCard = async (payload) =>
    (await judgeSaveCardCallable(payload)).data;
